import paths from 'routes/paths';

/**
 * Permissões do painel do produtor para quem entra por uma equipe. As chaves
 * são as mesmas da API (`PanelPermissions`); quem decide de verdade é a API,
 * que nega a rota sem a permissão. Aqui elas só servem para o painel não
 * oferecer o que vai ser recusado.
 */
export const PERMISSIONS = {
    DASHBOARD_VIEW: 'dashboard.view',
    EVENTS_VIEW: 'events.view',
    EVENTS_CREATE: 'events.create',
    EVENTS_EDIT: 'event.edit',
    EVENTS_DELETE: 'events.delete',
    EVENTS_SALES_VIEW: 'event.sales.view',
    PARTICIPANTS_VIEW: 'participants.view',
    CHECKIN_TICKETS: 'ticket',
    CHECKIN_PRODUCTS: 'product',
    ORDERS_VIEW: 'orders.view',
    ORDERS_MANAGE: 'orders.manage',
    PRODUCTS_VIEW: 'products.view',
    PRODUCTS_MANAGE: 'products.manage',
    COUPONS_VIEW: 'coupons.view',
    COUPONS_MANAGE: 'coupons.manage',
    CUSTOMERS_VIEW: 'customers.view',
    CAMPAIGNS_VIEW: 'campaigns.view',
    CAMPAIGNS_SEND: 'campaigns.send',
    FINANCIAL_VIEW: 'financial.view',
    FINANCIAL_WITHDRAW: 'financial.withdraw',
    FINANCIAL_BANK_ACCOUNTS: 'financial.bank_accounts',
    FINANCIAL_INVOICES: 'financial.invoices',
    TEAMS_VIEW: 'teams.view',
    TEAMS_MANAGE: 'teams.manage',
    PROFILE_VIEW: 'profile.view',
    PROFILE_MANAGE: 'profile.manage',
    AFFILIATE_VIEW: 'affiliate.view',
};

/**
 * Página do painel => permissões que a liberam (qualquer uma). `null` é
 * página aberta a todo membro. Página que não está aqui (a do painel de
 * estabelecimento, por exemplo) não abre para quem entrou por equipe. A
 * ordem é a do menu e define para onde o membro cai ao entrar.
 */
export const ROUTE_PERMISSIONS = {
    [paths.dashboard.statistics]: [PERMISSIONS.DASHBOARD_VIEW],
    [paths.dashboard.events]: [PERMISSIONS.EVENTS_VIEW],
    [paths.dashboard.checkin]: [PERMISSIONS.CHECKIN_TICKETS, PERMISSIONS.CHECKIN_PRODUCTS],
    [paths.dashboard.orders]: [PERMISSIONS.ORDERS_VIEW],
    [paths.dashboard.products]: [PERMISSIONS.PRODUCTS_VIEW],
    [paths.dashboard.customers]: [PERMISSIONS.CUSTOMERS_VIEW],
    [paths.dashboard.campaigns]: [PERMISSIONS.CAMPAIGNS_VIEW],
    [paths.dashboard.financial]: [PERMISSIONS.FINANCIAL_VIEW],
    [paths.dashboard.teams]: [PERMISSIONS.TEAMS_VIEW],
    [paths.dashboard.profile]: [PERMISSIONS.PROFILE_VIEW],
    // A análise é do perfil de produtor: quem vê o perfil vê a análise dele.
    [paths.dashboard.instagram]: [PERMISSIONS.PROFILE_VIEW],
    [paths.dashboard.affiliate]: [PERMISSIONS.AFFILIATE_VIEW],
    [paths.dashboard.support]: null,
};

/** Quem entrou no painel por uma equipe (a API devolve `acting_as` em /users/me). */
export function isActingAsMember(user) {
    return !!user?.acting_as;
}

/** O produtor é dono de tudo; o membro só tem o que a equipe liberou. */
export function userCan(user, ...anyOf) {
    if (!isActingAsMember(user)) return true;
    if (anyOf.length === 0) return true;

    const granted = user.acting_as.permissions || [];

    return anyOf.some((permission) => granted.includes(permission));
}

export function canOpenRoute(user, route) {
    if (!isActingAsMember(user)) return true;

    const required = ROUTE_PERMISSIONS[route];

    if (required === undefined) return false;
    if (required === null) return true;

    return userCan(user, ...required);
}

/** Primeira página do menu que as permissões abrem, ou null se nenhuma. */
export function firstAllowedPanelPath(permissions = []) {
    const allowed = Object.entries(ROUTE_PERMISSIONS).find(([, required]) => (
        required !== null && required.some((permission) => permissions.includes(permission))
    ));

    return allowed ? allowed[0] : null;
}
